import { TheWestSection } from '@news-mono/common'
import {
    BreachScreenContext,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    SwgMetaInfo,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import { TheWestRegionalSections } from '@west-australian-newspapers/publication-types'
import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import { colors, getCartPayload, IconTickOutline } from '../../../..'
import {
    isTabletOrMobileViewport,
    useSection,
    useSectionMetaInfo,
} from '../../../../__helpers'
import { useAuthActions } from '../../../authentication/AuthenticationProvider'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { SubscriptionPriceObject } from '../../helpers/BreachWallTypes'
import type {
    BreachOptions,
    PackageOptions,
    SubscriptionPackageProps,
} from '../BreachWallContent/BreachWallContent'
import { ComputedPackageImage } from '../SubscriptionPackage/ComputedPackageImage'
import {
    RadioSubscriptionPriceDisplay,
    SingleSubscriptionPriceDisplay,
} from '../SubscriptionPriceDisplay/SubscriptionPriceDisplay'
import { StyledSubscriptionRadioRibbon } from '../SubscriptionPriceDisplay/SubscriptionPriceDisplay.styled'
import {
    StyledBenefitListContainer,
    StyledBenefitListItemContainer,
    StyledCancellationMessage,
    StyledImageAndHeaderContainer,
    StyledPackageImage,
    StyledRecommendedBanner,
    StyledSubscribeButton,
    StyledSubscribeButtonsContainer,
    StyledSubscriptionPackage,
    StyledSubscriptionPackageContainer,
    StyledSubscriptionPackageHeading,
    StyledSubscriptionPackageHeadingContainer,
    StyledSubscriptionPackageSubHeading,
    StyledSwgButton,
} from './AflSubscriptionPackage.styled'

const DEFAULT_BENEFIT_NAMES = [
    'Access to all digital content',
    'Regional online news',
    'The West app',
    'Daily digital edition newspaper',
    'Subscriber rewards',
    'Weekend papers delivered',
] as const

const DIGITAL_AFL_IMAGE = require('./images/afl_everyday_digital.webp')
const DIGITAL_AND_PRINT_AFL_IMAGE = require('./images/afl_everyday_digital_and_weekend_papers.webp')

export const AflSubscriptionPackage: React.FC<SubscriptionPackageProps> = ({
    breachDeal,
    breachCreativeName,
    breachType,
    onEvent,
    masthead = 'The West Australian',
    packageInfo,
    isRegionalOverride,
    desiredRedirect,
    isSwgPage = false,
}) => {
    const {
        heading,
        subHeading,
        ribbonTitle,
        ribbonOptions,
        packageTheme = 'default',
        buttonStyle,
        benefitOverride,
        type: subscriptionPackageType,
        isRecommended,
    } = packageInfo

    const isRadio = packageInfo.type === 'radio-list-subscriptions'
    const packagePrices = useMemo(
        () => (isRadio ? packageInfo.prices : [packageInfo]),
        [isRadio, packageInfo],
    )
    const defaultPackage = isRadio ? packageInfo.prices[0] : packageInfo
    const [selectedPrice, setSelectedPrice] = useState(defaultPackage)
    const { cancellationMessage, googleSKU, packagePath, offerCode } =
        selectedPrice

    // ---- Hooks ----
    const section = useSection<TheWestSection>()
    const sectionMeta = useSectionMetaInfo()
    const { onSubscribeClick } = useAuthActions()
    const isMobile = isTabletOrMobileViewport()

    // ---- Breach config options ----
    const isDigital = selectedPrice.packageType === 'digital'
    const isRegional = section in TheWestRegionalSections
    const breachName = section === 'genwest' ? 'default' : section

    // Builds the benefit list based on the provided benefits and any additional benefits from the selected price point
    // If no benefits are provided, defaults to DEFAULT_BENEFIT_NAMES
    const benefits = useMemo(() => {
        // Combined benefits uses customBenefits if provided, otherwise defaults to DEFAULT_BENEFIT_NAMES
        let combinedBenefits: string[] = benefitOverride ?? [
            ...DEFAULT_BENEFIT_NAMES,
        ]

        // Append any additional benefits from the individual price points
        packagePrices.forEach((pricePoint) => {
            if (pricePoint.additionalBenefits) {
                if (pricePoint === selectedPrice) {
                    combinedBenefits = [
                        ...combinedBenefits,
                        ...pricePoint.additionalBenefits,
                    ]
                }
            }
        })

        return combinedBenefits
    }, [benefitOverride, packagePrices, selectedPrice])

    const onPackageChange = useCallback(
        (pricePoint: SubscriptionPriceObject) => {
            const current = selectedPrice

            setSelectedPrice(pricePoint)

            onEvent({
                type: DataLayerEventName.billingPeriodChange,
                originator: 'SubscriptionPackage',
                payload: {
                    from: {
                        weeklyPrice: current.weeklyPrice,
                        rateCode: current.rateCode,
                        offerCode: current.offerCode,
                    },
                    to: {
                        weeklyPrice: pricePoint.weeklyPrice,
                        rateCode: pricePoint.rateCode,
                        offerCode: pricePoint.offerCode,
                    },
                },
            })
        },
        // onEvent can cause excessive re-renders if included in deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedPrice],
    )

    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context
    const breachScreenType: BreachScreenType = 'Premium - Inline'

    // ---- Subscribe with Google ----
    let swgMetaInfo: SwgMetaInfo | undefined = undefined
    const buttonRef = useRef<HTMLButtonElement>(null)
    // Matches swg-breach-albany-advertiser etc
    const isSWGBreachInterfaceEnabled = useFeature(`swg-breach-${breachName}`)
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const showSwG =
        subscribeWithGoogleEnabled && isSWGBreachInterfaceEnabled && googleSKU
    if (desiredRedirect) {
        swgMetaInfo = { desiredRedirect }
    }
    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        googleSKU ?? 'everyday_digital_4weeks',
        sectionMeta,
        breachContext,
        swgMetaInfo,
    )

    const addToCartPayload = getCartPayload(
        { price: selectedPrice, package: packageInfo },
        masthead,
        { quantity: selectedPrice.quantity },
    )

    const packageOptions: PackageOptions = {
        theme: packageTheme,
        type: selectedPrice.packageType,
        gridColumnPosition: packageInfo.columnPosition,
        isRecommended,
    }
    const breachOptions: BreachOptions = {
        useBreachDealStyle: isRegionalOverride,
        isRegional: isRegional,
        breachType,
    }

    return (
        <StyledSubscriptionPackageContainer
            packageOptions={packageOptions}
            breachOptions={breachOptions}
        >
            {/* Recommended Package Banner */}
            {isRecommended && (
                <StyledRecommendedBanner>
                    {isMobile ? 'Most Popular' : 'Most Popular -\u00A0'}
                    {!isMobile && <em>Save 25%</em>}
                </StyledRecommendedBanner>
            )}
            <StyledSubscriptionPackage>
                <StyledImageAndHeaderContainer>
                    {/* Package Image */}
                    {isDigital ? (
                        <StyledPackageImage
                            src={DIGITAL_AFL_IMAGE}
                            alt="The West Australian digital subscription package image"
                            role="presentation"
                        />
                    ) : selectedPrice.packageType === 'digitalprint' ? (
                        <StyledPackageImage
                            src={DIGITAL_AND_PRINT_AFL_IMAGE}
                            alt="The West Australian digital & print subscription package image"
                            role="presentation"
                        />
                    ) : (
                        <ComputedPackageImage section={section} />
                    )}
                    {/* Package Heading */}
                    <StyledSubscriptionPackageHeadingContainer>
                        {/* BluePillLight is the only ribbon that shows above the heading currently */}
                        {ribbonOptions &&
                            ribbonTitle &&
                            ribbonOptions === 'bluePillLight' && (
                                <StyledSubscriptionRadioRibbon
                                    ribbonOptions={ribbonOptions}
                                >
                                    {ribbonTitle}
                                </StyledSubscriptionRadioRibbon>
                            )}
                        <div>
                            {subHeading && (
                                <StyledSubscriptionPackageSubHeading>
                                    {subHeading}
                                </StyledSubscriptionPackageSubHeading>
                            )}
                            <StyledSubscriptionPackageHeading>
                                {heading}
                            </StyledSubscriptionPackageHeading>
                        </div>
                    </StyledSubscriptionPackageHeadingContainer>
                </StyledImageAndHeaderContainer>
                {/* Display price points */}
                {subscriptionPackageType === 'single-subscription' ? (
                    <SingleSubscriptionPriceDisplay
                        subscriptionPackage={packageInfo}
                        packageOptions={packageOptions}
                        breachOptions={breachOptions}
                    />
                ) : (
                    <RadioSubscriptionPriceDisplay
                        subscriptionPackage={packageInfo}
                        packageOptions={packageOptions}
                        breachOptions={breachOptions}
                        selectedPricePoint={selectedPrice}
                        packagePrices={packagePrices}
                        setSelectedPricePoint={onPackageChange}
                    />
                )}
                <StyledSubscribeButtonsContainer>
                    {!isSwgPage && (
                        <StyledSubscribeButton
                            packageOptions={packageOptions}
                            breachOptions={breachOptions}
                            buttonStyle={buttonStyle}
                            type="button"
                            title="Subscribe Button"
                            aria-label="Subscribe button"
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    breachScreenType,
                                    1,
                                    addToCartPayload,
                                )()
                                onSubscribeClick({
                                    packagePath,
                                    offerCode,
                                    callToAction: click_origin_breach_screen,
                                    breachScreenType: breachScreenType,
                                })
                            }}
                        >
                            Subscribe
                        </StyledSubscribeButton>
                    )}
                    {isDigital && showSwG && (
                        <StyledSwgButton
                            ref={buttonRef}
                            type="button"
                            aria-label="Subscribe with google button"
                        />
                    )}
                </StyledSubscribeButtonsContainer>
                {/* Benefit Lists */}
                <StyledBenefitListContainer>
                    {benefits.map((benefit) => (
                        <StyledBenefitListItemContainer key={benefit}>
                            <IconTickOutline
                                width={24}
                                height={24}
                                color={colors.thewest.westblue}
                            />
                            <p>{benefit}</p>
                        </StyledBenefitListItemContainer>
                    ))}
                </StyledBenefitListContainer>
                {cancellationMessage && (
                    <StyledCancellationMessage>
                        {cancellationMessage}
                    </StyledCancellationMessage>
                )}
            </StyledSubscriptionPackage>
        </StyledSubscriptionPackageContainer>
    )
}

AflSubscriptionPackage.displayName = 'AflSubscriptionPackage'
